import { YourCostShareOopMaxRules } from "#/components/views/reimbursements/educationmodal/YourCostShareOopMaxRules"
import { YourCostShareQmeOopMaxRules } from "#/components/views/reimbursements/educationmodal/YourCostShareQmeAndOopMaxRules"
import { YourCostShareQmeRule } from "#/components/views/reimbursements/educationmodal/YourCostShareQmeRules"
import { YourCostShareSingleRule } from "#/components/views/reimbursements/educationmodal/YourCostShareSingleRule"
import { ReimbursementRule } from "#/lib/carrot-api/types/Subsidy"
import { getCompanyDisplayName } from "#/redux/reducers/companyInfo"
import { DbgPaths } from "#/utils/Paths"
import React, { FC } from "react"
import { FormattedMessage } from "react-intl"
import { useSelector } from "react-redux"
import { useReimbursementRulePercentages } from "../hooks/useReimbursementRulePercentages"
import { DialogTitle, Stack, Typography, Link, InternalIcon } from "@carrotfertility/carotene-core"

export type YourCostShareInformationalProps = {
  reimbursementRules: ReimbursementRule[]
  isDefaultCategory: boolean
  hasCarrotCard: boolean
}

const YourCostShareInformational: FC<YourCostShareInformationalProps> = ({
  reimbursementRules,
  isDefaultCategory,
  hasCarrotCard
}) => {
  const companyDisplayName = useSelector(getCompanyDisplayName)
  const rules = useReimbursementRulePercentages(reimbursementRules)
  const getPercentages = rules.getPercentages
  const showQmesLink = rules.hasQmeRule

  return (
    <Stack display="flex" gap={(theme) => theme.tokens.spacing.lg}>
      <DialogTitle id="cost-share-modal-header">
        <FormattedMessage defaultMessage="About your cost share" />
      </DialogTitle>
      {rules.isSingleRule ? (
        <Typography>
          <FormattedMessage defaultMessage="Cost sharing is when your benefit sponsor pays for a percentage of your eligible expenses with Carrot." />
        </Typography>
      ) : rules.hasOopMaxRule && !rules.hasQmeRule ? (
        <>
          <Typography>
            <FormattedMessage defaultMessage="Cost sharing is when your benefit sponsor pays for a percentage of your eligible expenses with Carrot." />
          </Typography>
          <Typography>
            <FormattedMessage
              defaultMessage="Your cost share amount depends on your insurance status."
              description="Explanation shown when cost share varies by insurance out-of-pocket maximum status"
            />
          </Typography>
        </>
      ) : rules.hasQmeRule && !rules.hasOopMaxRule ? (
        <>
          <Typography>
            <FormattedMessage defaultMessage="Cost sharing is when your benefit sponsor pays for a percentage of your eligible expenses with Carrot." />
          </Typography>
          <Typography>
            <FormattedMessage
              defaultMessage="Your cost share amount depends on your expense type."
              description="Explanation shown when cost share varies by qualified vs non-qualified medical expense type"
            />
          </Typography>
        </>
      ) : (
        <Typography>
          <FormattedMessage
            defaultMessage="The amount you pay for eligible expenses in this category depends on your insurance status and expense type."
            description="Explanation shown when cost share varies by both insurance status and expense type"
          />
        </Typography>
      )}
      {showQmesLink && (
        <Link
          href={DbgPaths.QUALIFIED_MEDICAL_EXPENSES}
          target={"_blank"}
          endIcon={<InternalIcon fontSize="small" color="primary" />}
        >
          <FormattedMessage
            defaultMessage="Learn more about qualified medical expenses (QMEs)"
            description="Link text to learn about qualified medical expenses"
          />
        </Link>
      )}
      {rules.isSingleRule && (
        <YourCostShareSingleRule
          companyDisplayName={companyDisplayName}
          isDefaultCategory={isDefaultCategory}
          getPercentages={getPercentages}
        />
      )}
      {rules.hasQmeRule && !rules.hasOopMaxRule && (
        <YourCostShareQmeRule
          companyDisplayName={companyDisplayName}
          isDefaultCategory={isDefaultCategory}
          getPercentages={getPercentages}
        />
      )}
      {rules.hasOopMaxRule && !rules.hasQmeRule && (
        <YourCostShareOopMaxRules
          companyDisplayName={companyDisplayName}
          isDefaultCategory={isDefaultCategory}
          getPercentages={getPercentages}
        />
      )}
      {rules.hasQmeRule && rules.hasOopMaxRule && (
        <YourCostShareQmeOopMaxRules
          companyDisplayName={companyDisplayName}
          isDefaultCategory={isDefaultCategory}
          getPercentages={getPercentages}
        />
      )}
      <Typography>
        <FormattedMessage defaultMessage="Carrot funds you use may also be subject to bank fees, taxes, and other plan details." />
      </Typography>
      {hasCarrotCard && (
        <Link
          href={"/your-benefit-guide/carrot-funds/carrot-card"}
          target={"_blank"}
          endIcon={<InternalIcon fontSize="small" color="primary" />}
        >
          <FormattedMessage defaultMessage="Learn how cost sharing works with Carrot Card" />
        </Link>
      )}
    </Stack>
  )
}

export { YourCostShareInformational }
